import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
  useRef,
} from 'react';
import { useAuth } from './auth';
import { enviarNotificacao, ordersReducer } from '../pages/Pedido/utils';
import openSocket from 'socket.io-client';
import { bindDesktopConfigurationEvents } from '../services/desktop/sync';
import { useConfig } from './config';
import sound1 from '../assets/sounds/notification.mp3';
import sound2 from '../assets/sounds/notification2.mp3';
import sound3 from '../assets/sounds/notification3.mp3';
import sound4 from '../assets/sounds/notification4.mp3';
import sound5 from '../assets/sounds/notification5.mp3';

const soundMap = {
  1: sound1,
  2: sound2,
  3: sound3,
  4: sound4,
  5: sound5,
};

const NotificationOrderContext = createContext({});

export const NotificationOrderProvider = ({ children }) => {
  const { id_empresa } = useAuth();
  const { config } = useConfig();
  const { user } = useAuth();
  const audioRef = useRef(null);
  const [pedidosLista, dispatch] = useReducer(ordersReducer, []);
  const [hasPermission, setHasPermission] = useState(null);

  useEffect(() => {
    const songNumber = config.NotificationPushSong || 1;
    const soundFile = soundMap[songNumber];

    if (!soundFile) {
      console.error(
        `Arquivo de som não encontrado para o valor: ${songNumber}`,
      );
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }

    const newAudio = new Audio(soundFile);
    newAudio.preload = 'auto';
    newAudio.load();

    audioRef.current = newAudio;

    return () => {
      newAudio.pause();
    };
  }, [config.NotificationPushSong]);

  const playAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else {
      console.warn('Objeto de áudio ainda não foi carregado.');
    }
  }, []);

  const requestPermission = async () => {
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        alert('Oba! Notificações ativadas.');
        setHasPermission(true);
        new Notification('Boas-vindas!', {
          body: 'Agora você está conectado.',
        });
      } else {
        setHasPermission(false);
        alert(
          'Que pena. Você pode ativar nas configurações do navegador mais tarde.',
        );
      }
    } else if (Notification.permission === 'granted') {
      setHasPermission(true);
      alert('Você já ativou as notificações. Tudo certo!');
    } else if (Notification.permission === 'denied') {
      setHasPermission(false);
      alert(
        'Você bloqueou as notificações. Para ativar, mude nas configurações do seu navegador.',
      );
    }
  };

  useEffect(() => {
    console.log(
      'Estabelecendo conexão de socket para notificações de pedidos...',
    );

    const socket = openSocket(process.env.REACT_APP_BACKEND_URL, {
      transports: ['websocket'],
      reconnectionAttempts: 5,
      query: { companyId: id_empresa },
    });

    const unbindDesktop = bindDesktopConfigurationEvents(socket, id_empresa);

    socket.on(`pedido-${id_empresa}`, (data) => {
      if (data.type === 'create') {
        dispatch({ type: 'ADD_PEDIDO', payload: data.payload });
        playAudio();
        console.log('Novo pedido recebido via socket:', data.payload);
        enviarNotificacao(data.payload);
      }
      if (data.type === 'update') {
        dispatch({ type: 'UPDATE_PEDIDO', payload: data.payload });
      }
      if (data.type === 'delete') {
        dispatch({ type: 'DELETE_PEDIDO', payload: data.payload.Id });
      }
    });
    return () => {
      unbindDesktop();
      socket.disconnect();
    };
  }, [id_empresa, playAudio, user]);

  useEffect(() => {
    if (Notification.permission === 'granted') {
      setHasPermission(true);
    } else if (Notification.permission === 'denied') {
      setHasPermission(false);
    }
  }, []);

  return (
    <NotificationOrderContext.Provider
      value={{ hasPermission, requestPermission, pedidosLista, dispatch }}
    >
      {children}
    </NotificationOrderContext.Provider>
  );
};

export function useNotificationOrder() {
  const context = useContext(NotificationOrderContext);

  if (!context) {
    throw new Error(
      'NotificationOrderContext must be used within an NotificationOrderContext',
    );
  }
  return context;
}
